import { useState } from "react";
import { toast } from "sonner";
import { Newspaper, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { fmtNewsDate } from "../NewsSection";

const EMPTY = { date_label: "", title: "", body: "" };

export default function NewsCard({ posts, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.title.trim()) return toast.error("Give the update a headline");
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/news/${editingId}`, form);
        toast.success("Update saved — the change is live on the site");
      } else {
        await api.post("/news", form);
        toast.success("Posted — visitors can read it on the News page");
      }
      setForm(EMPTY);
      setEditingId(null);
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ date_label: p.date_label || "", title: p.title || "", body: p.body || "" });
    document
      .getElementById("news-form-fields")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this update? Visitors will no longer see it.")) return;
    try {
      await api.delete(`/news/${p.id}`);
      if (editingId === p.id) {
        setEditingId(null);
        setForm(EMPTY);
      }
      toast.success("Update deleted");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  return (
    <section data-testid="news-manager" className="border border-line bg-panel p-6 md:p-8">
      <div className="flex items-center gap-3">
        <Newspaper size={14} className="text-ochre" />
        <p className="eyebrow !text-ochre/90">News</p>
      </div>
      <h2 className="font-display text-2xl mt-2">Studio Journal</h2>
      <p className="text-sm text-mute mt-3 leading-relaxed">
        Post news your collectors care about — upcoming shows, new works, press.
        The latest three appear on the gallery page, and everything lives on the
        News page, newest first.
      </p>

      <form onSubmit={submit} id="news-form-fields" data-testid="news-form" className="mt-6 space-y-5">
        <div className="grid md:grid-cols-[220px_1fr] gap-5">
          <div>
            <label className="field-label" htmlFor="news-date">Date shown to visitors</label>
            <input
              id="news-date"
              value={form.date_label}
              onChange={set("date_label")}
              placeholder="March 14, 2026"
              className="field-input"
              data-testid="news-date-input"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="news-title">Headline</label>
            <input
              id="news-title"
              value={form.title}
              onChange={set("title")}
              placeholder="Solo show at Midtown Gallery — opening night"
              className="field-input"
              data-testid="news-title-input"
            />
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor="news-body">The story</label>
          <textarea
            id="news-body"
            rows={3}
            value={form.body}
            onChange={set("body")}
            placeholder="Join me for the opening of…"
            className="field-input resize-y leading-relaxed"
            data-testid="news-body-input"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={busy}
            data-testid="news-submit-button"
            className="border border-gold/60 bg-gold text-ink font-meta text-[11px] uppercase tracking-[0.3em] px-8 py-4 hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : editingId ? "Save Update" : "Post Update"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="news-cancel-button"
              className="border border-line font-meta text-[11px] uppercase tracking-[0.3em] px-8 py-4 text-mute hover:text-bone hover:border-bone/40 transition-colors duration-300"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {posts.length > 0 && (
        <ul className="mt-8 space-y-4">
          {posts.map((p) => (
            <li
              key={p.id}
              data-testid={`studio-news-${p.id}`}
              className={`border bg-elevated p-4 ${editingId === p.id ? "border-ochre/60" : "border-line"}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-meta text-[9px] uppercase tracking-[0.25em] text-gold">
                    {fmtNewsDate(p)}
                  </p>
                  <p className="font-display text-lg leading-tight mt-1">{p.title}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => startEdit(p)}
                    disabled={busy}
                    data-testid={`news-edit-button-${p.id}`}
                    title="Edit"
                    className="border border-line p-2 text-mute hover:text-ochre hover:border-ochre/50 transition-colors duration-300"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    disabled={busy}
                    data-testid={`news-delete-button-${p.id}`}
                    title="Delete"
                    className="border border-line p-2 text-mute hover:text-bone hover:border-oxblood/70 transition-colors duration-300"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              {p.body && <p className="text-sm text-mute mt-2">{p.body}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
